<script setup>
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Draggable from 'vuedraggable';
import { useUISettings } from 'dashboard/composables/useUISettings';
import KanbanColumn from './KanbanColumn.vue';

const props = defineProps({
  conversations: { type: Array, default: () => [] },
  label: { type: String, default: '' },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
});

const { t } = useI18n();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const groupBy = computed(() => uiSettings.value.kanban_group_by || 'status');

// ── Status grouping ──────────────────────────────────────────────────────────

const STATUS_COLUMNS = [
  {
    key: 'open',
    titleKey: 'CONVERSATION.KANBAN.COLUMN_OPEN',
    dotColor: 'bg-green-500',
  },
  {
    key: 'pending',
    titleKey: 'CONVERSATION.KANBAN.COLUMN_PENDING',
    dotColor: 'bg-amber-500',
  },
  {
    key: 'snoozed',
    titleKey: 'CONVERSATION.KANBAN.COLUMN_SNOOZED',
    dotColor: 'bg-violet-500',
  },
  {
    key: 'resolved',
    titleKey: 'CONVERSATION.KANBAN.COLUMN_RESOLVED',
    dotColor: 'bg-n-slate-6',
  },
];

const statusColumns = computed(() =>
  STATUS_COLUMNS.map(col => ({
    id: col.key,
    title: t(col.titleKey),
    dotColor: col.dotColor,
    conversations: props.conversations.filter(c => c.status === col.key),
  }))
);

// ── Agent grouping ───────────────────────────────────────────────────────────

const agentColumns = computed(() => {
  const agentMap = new Map();

  // Unassigned always first
  agentMap.set('unassigned', {
    id: 'unassigned',
    title: t('CONVERSATION.KANBAN.UNASSIGNED'),
    dotColor: 'bg-n-slate-6',
    avatar: null,
    conversations: [],
  });

  props.conversations.forEach(conv => {
    const assignee = conv.meta?.assignee;
    if (!assignee) {
      agentMap.get('unassigned').conversations.push(conv);
    } else {
      if (!agentMap.has(assignee.id)) {
        agentMap.set(assignee.id, {
          id: assignee.id,
          title: assignee.name,
          dotColor: 'bg-n-brand',
          avatar: assignee.avatar_url,
          conversations: [],
        });
      }
      agentMap.get(assignee.id).conversations.push(conv);
    }
  });

  // Sort: unassigned first, then agents by count desc, then alphabetically
  return Array.from(agentMap.values()).sort((a, b) => {
    if (a.id === 'unassigned') return -1;
    if (b.id === 'unassigned') return 1;
    if (b.conversations.length !== a.conversations.length) {
      return b.conversations.length - a.conversations.length;
    }
    return a.title.localeCompare(b.title);
  });
});

// ── Team grouping ────────────────────────────────────────────────────────────

const teamColumns = computed(() => {
  const teamMap = new Map();

  // No team always first
  teamMap.set('no_team', {
    id: 'no_team',
    title: t('CONVERSATION.KANBAN.NO_TEAM'),
    dotColor: 'bg-n-slate-6',
    conversations: [],
  });

  props.conversations.forEach(conv => {
    const team = conv.meta?.team;
    if (!team?.id) {
      teamMap.get('no_team').conversations.push(conv);
    } else {
      if (!teamMap.has(team.id)) {
        teamMap.set(team.id, {
          id: team.id,
          title: team.name,
          dotColor: 'bg-n-brand',
          conversations: [],
        });
      }
      teamMap.get(team.id).conversations.push(conv);
    }
  });

  return Array.from(teamMap.values()).sort((a, b) => {
    if (a.id === 'no_team') return -1;
    if (b.id === 'no_team') return 1;
    if (b.conversations.length !== a.conversations.length) {
      return b.conversations.length - a.conversations.length;
    }
    return a.title.localeCompare(b.title);
  });
});

// ── Label grouping ───────────────────────────────────────────────────────────

const labelColumns = computed(() => {
  const accountLabels = store.getters['labels/getLabels'];
  const cols = [];

  accountLabels.forEach(label => {
    const conversations = props.conversations.filter(
      c => Array.isArray(c.labels) && c.labels.includes(label.title)
    );
    if (conversations.length > 0) {
      cols.push({
        id: `label_${label.id}`,
        title: label.title,
        dotColor: '',
        dotStyle: { backgroundColor: label.color },
        conversations,
      });
    }
  });

  // Sort by count desc, then alphabetically
  cols.sort((a, b) => {
    if (b.conversations.length !== a.conversations.length) {
      return b.conversations.length - a.conversations.length;
    }
    return a.title.localeCompare(b.title);
  });

  // "Sem etiqueta" at the end
  const untagged = props.conversations.filter(
    c => !Array.isArray(c.labels) || c.labels.length === 0
  );
  if (untagged.length > 0) {
    cols.push({
      id: 'no_label',
      title: t('CONVERSATION.KANBAN.NO_LABEL'),
      dotColor: 'bg-n-slate-6',
      dotStyle: null,
      conversations: untagged,
    });
  }

  return cols;
});

const columns = computed(() => {
  if (groupBy.value === 'agent') return agentColumns.value;
  if (groupBy.value === 'team') return teamColumns.value;
  if (groupBy.value === 'label') return labelColumns.value;
  return statusColumns.value;
});

// ── Column order (drag and drop) ─────────────────────────────────────────────

const localColumns = ref([]);

const orderKey = computed(() => `kanban_columns_order_${groupBy.value}`);

// Sync localColumns whenever the base columns or saved order change
watchEffect(() => {
  const savedOrder = uiSettings.value[orderKey.value] || [];
  const cols = columns.value;

  if (!savedOrder.length) {
    localColumns.value = [...cols];
    return;
  }

  const sorted = [...cols].sort((a, b) => {
    const ai = savedOrder.indexOf(String(a.id));
    const bi = savedOrder.indexOf(String(b.id));
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  localColumns.value = sorted;
});

const onDragEnd = () => {
  updateUISettings({
    [orderKey.value]: localColumns.value.map(c => String(c.id)),
  });
};
</script>

<template>
  <Draggable
    :list="localColumns"
    animation="200"
    ghost-class="kanban-col-ghost"
    handle=".kanban-drag-handle"
    item-key="id"
    class="flex flex-row gap-3 h-full px-3 py-3 min-w-max"
    @end="onDragEnd"
  >
    <template #item="{ element: col }">
      <KanbanColumn
        :title="col.title"
        :dot-color="col.dotColor"
        :dot-style="col.dotStyle || null"
        :conversations="col.conversations"
        :label="label"
        :team-id="teamId"
        :folders-id="foldersId"
        :conversation-type="conversationType"
        :show-assignee="groupBy !== 'agent'"
      />
    </template>
  </Draggable>
</template>

<style scoped>
.kanban-col-ghost {
  opacity: 0.4;
}
</style>
